import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { VIEW_LABEL, VIEW_ORDER, type ExposureView } from '../lib/exposure-status';
import { formatMt, VIEW_HEADER_COLOR } from '../lib/exposure-styles';
import { TICKER_LABEL } from '../lib/risk-summary-derivation';

// Hedge-book chip filter — All plus the three actionable views. "No Action"
// rows are hidden by the backend's filter, so the chip is intentionally
// omitted (mirrors launchpad's SummaryStrip behaviour).
const CHIP_VIEWS: { id: 'all' | ExposureView; label: string }[] = [
  { id: 'all', label: 'All' },
  ...VIEW_ORDER.filter((v) => v !== 'no-action').map((v) => ({ id: v, label: VIEW_LABEL[v] })),
];

interface SummaryStripProps {
  total: number;
  counts: Record<ExposureView, number>;
  openVolumeMt: number;
  activeView: 'all' | ExposureView;
  onChange: (v: 'all' | ExposureView) => void;
}

export function SummaryStrip({
  total,
  counts,
  openVolumeMt,
  activeView,
  onChange,
}: Readonly<SummaryStripProps>) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card px-4 py-3">
      <div className="flex flex-wrap items-center gap-1">
        {CHIP_VIEWS.map((chip) => (
          <Chip
            key={chip.id}
            chip={chip}
            count={chip.id === 'all' ? total : counts[chip.id]}
            isActive={chip.id === activeView}
            onClick={() => {
              onChange(chip.id);
            }}
          />
        ))}
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>Open volume</span>
        <span className="text-sm font-semibold tabular-nums text-foreground">
          {formatMt(openVolumeMt)} MT
        </span>
      </div>
    </div>
  );
}

interface ChipProps {
  chip: { id: 'all' | ExposureView; label: string };
  count: number;
  isActive: boolean;
  onClick: () => void;
}

function Chip({ chip, count, isActive, onClick }: Readonly<ChipProps>) {
  const countColor = !isActive && chip.id !== 'all' ? VIEW_HEADER_COLOR[chip.id] : undefined;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className={cn(
        'inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
        isActive
          ? 'bg-primary text-primary-foreground shadow-sm'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      <span className={cn('font-semibold tabular-nums', countColor)}>{count.toString()}</span>
      <span className="font-normal">{chip.label}</span>
    </button>
  );
}

interface FilterRowProps {
  search: string;
  onSearchChange: (v: string) => void;
  assets: readonly string[];
  asset: string;
  onAssetChange: (v: string) => void;
  visibleCount: number;
}

export function FilterRow({
  search,
  onSearchChange,
  assets,
  asset,
  onAssetChange,
  visibleCount,
}: Readonly<FilterRowProps>) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative w-full max-w-sm flex-1">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          value={search}
          onChange={(e) => {
            onSearchChange(e.target.value);
          }}
          placeholder="Search order or counterparty…"
          aria-label="Search hedgeable orders"
          className="h-9 pl-9 text-base sm:text-sm"
        />
      </div>
      {assets.length > 1 && (
        <Select
          value={asset}
          aria-label="Filter by asset"
          className="h-9 w-auto min-w-[120px] text-base sm:text-sm"
          onChange={(e) => {
            onAssetChange(e.target.value);
          }}
        >
          <option value="all">All assets</option>
          {assets.map((code) => (
            <option key={code} value={code}>
              {code} — {TICKER_LABEL[code] ?? code}
            </option>
          ))}
        </Select>
      )}
      <span className="ml-auto text-xs tabular-nums text-muted-foreground">
        {visibleCount.toString()} order{visibleCount === 1 ? '' : 's'}
      </span>
    </div>
  );
}
